<script setup>
import {
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
  reactive,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { vOnClickOutside } from '@vueuse/components';
import Draggable from 'vuedraggable';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import CategoryAssignmentModal from './CategoryAssignmentModal.vue';
import { useStore } from 'dashboard/composables/store';
import { ATTRIBUTE_CATEGORIES } from './constants';

const store = useStore();
const { t } = useI18n();

const records = computed(
  () => store.getters['customAttributeCategories/getRecords'] || []
);
const uiFlags = computed(
  () => store.getters['customAttributeCategories/getUIFlags']
);

const parentCategories = ATTRIBUTE_CATEGORIES;

const PARENT_CATEGORY_LABELS = {
  INFO: 'ATTRIBUTES_MGMT.ATTRIBUTE_CATEGORIES.INFO',
  AV: 'ATTRIBUTES_MGMT.ATTRIBUTE_CATEGORIES.AV',
  CATERING: 'ATTRIBUTES_MGMT.ATTRIBUTE_CATEGORIES.CATERING',
  SECURITY: 'ATTRIBUTES_MGMT.ATTRIBUTE_CATEGORIES.SECURITY',
  TECHNICAL: 'ATTRIBUTES_MGMT.ATTRIBUTE_CATEGORIES.TECHNICAL',
  CREW: 'ATTRIBUTES_MGMT.ATTRIBUTE_CATEGORIES.CREW',
  OTHER: 'ATTRIBUTES_MGMT.ATTRIBUTE_CATEGORIES.OTHER',
};

const parentCategoryKeyToValue = {
  INFO: 'info',
  AV: 'av',
  CATERING: 'catering',
  SECURITY: 'security',
  TECHNICAL: 'technical',
  CREW: 'crew',
  OTHER: 'other',
};

const formatParentLabel = category =>
  t(PARENT_CATEGORY_LABELS[category.key] || category.key);

const form = reactive({
  name: '',
  parent_category: 'info',
  active: true,
});
const editingCategoryId = ref(null);
const isPopoverOpen = ref(false);
const popoverRef = ref(null);
const showDeleteModal = ref(false);
const selectedCategory = ref(null);
const popoverPosition = reactive({ top: 0, left: 0 });
const popoverTriggerRect = ref(null);
const assignmentModal = ref(null);

const openAssignmentModal = (categoryKey, categoryLabel) => {
  assignmentModal.value = { categoryKey, categoryLabel };
};

const closeAssignmentModal = () => {
  assignmentModal.value = null;
};

const resetForm = () => {
  form.name = '';
  form.parent_category = 'info';
  form.active = true;
  editingCategoryId.value = null;
};

const PANEL_WIDTH = 380;
const VIEWPORT_SPACING = 8;

const setPopoverPositionFromTrigger = triggerEl => {
  if (!triggerEl) return;
  const rect = triggerEl.getBoundingClientRect();
  popoverTriggerRect.value = rect;
  popoverPosition.top = rect.bottom + VIEWPORT_SPACING;
  popoverPosition.left = Math.min(
    Math.max(VIEWPORT_SPACING, rect.right - PANEL_WIDTH),
    window.innerWidth - PANEL_WIDTH - VIEWPORT_SPACING
  );
};

const updatePopoverPosition = () => {
  if (!popoverTriggerRect.value || !popoverRef.value) return;
  const triggerRect = popoverTriggerRect.value;
  const panelRect = popoverRef.value.getBoundingClientRect();
  const spaceBelow = window.innerHeight - triggerRect.bottom - VIEWPORT_SPACING;
  const spaceAbove = triggerRect.top - VIEWPORT_SPACING;
  if (panelRect.height > spaceBelow && spaceAbove > spaceBelow) {
    popoverPosition.top = Math.max(
      VIEWPORT_SPACING,
      triggerRect.top - panelRect.height - VIEWPORT_SPACING
    );
    return;
  }
  popoverPosition.top = Math.min(
    triggerRect.bottom + VIEWPORT_SPACING,
    window.innerHeight - panelRect.height - VIEWPORT_SPACING
  );
};

const openCreateFlapout = async event => {
  if (editingCategoryId.value !== null) {
    resetForm();
  }
  setPopoverPositionFromTrigger(event?.currentTarget);
  isPopoverOpen.value = true;
  await nextTick();
  updatePopoverPosition();
};

const openEditFlapout = async (category, event) => {
  if (editingCategoryId.value !== category.id) {
    form.name = category.name;
    form.parent_category = category.parent_category;
    form.active = category.active;
    editingCategoryId.value = category.id;
  }
  setPopoverPositionFromTrigger(event?.currentTarget);
  isPopoverOpen.value = true;
  await nextTick();
  updatePopoverPosition();
};

const dismissFlapout = () => {
  isPopoverOpen.value = false;
};

const cancelFlapout = () => {
  isPopoverOpen.value = false;
  resetForm();
};

const saveCategory = async () => {
  if (!form.name.trim()) return;
  try {
    if (editingCategoryId.value) {
      await store.dispatch('customAttributeCategories/update', {
        id: editingCategoryId.value,
        ...form,
      });
      useAlert(t('ATTRIBUTES_MGMT.CATEGORIES.UPDATE.SUCCESS'));
    } else {
      await store.dispatch('customAttributeCategories/create', { ...form });
      useAlert(t('ATTRIBUTES_MGMT.CATEGORIES.CREATE.SUCCESS'));
    }
    cancelFlapout();
  } catch {
    useAlert(
      editingCategoryId.value
        ? t('ATTRIBUTES_MGMT.CATEGORIES.UPDATE.ERROR')
        : t('ATTRIBUTES_MGMT.CATEGORIES.CREATE.ERROR')
    );
  }
};

const openDeleteModal = category => {
  selectedCategory.value = category;
  showDeleteModal.value = true;
};

const closeDeleteModal = () => {
  showDeleteModal.value = false;
  selectedCategory.value = null;
};

const removeCategory = async () => {
  if (!selectedCategory.value) return;
  try {
    await store.dispatch(
      'customAttributeCategories/delete',
      selectedCategory.value.id
    );
    useAlert(t('ATTRIBUTES_MGMT.CATEGORIES.DELETE.SUCCESS'));
    closeDeleteModal();
  } catch {
    useAlert(t('ATTRIBUTES_MGMT.CATEGORIES.DELETE.ERROR'));
  }
};

const localCategoriesByParent = ref({});

watch(
  records,
  newRecords => {
    const grouped = {};
    parentCategories.forEach(pc => {
      const key = parentCategoryKeyToValue[pc.key];
      grouped[key] = [];
    });
    newRecords.forEach(cat => {
      if (grouped[cat.parent_category]) {
        grouped[cat.parent_category].push(cat);
      }
    });
    Object.keys(grouped).forEach(key => {
      grouped[key].sort(
        (a, b) =>
          a.position - b.position ||
          new Date(a.created_at) - new Date(b.created_at) ||
          a.id - b.id
      );
    });
    localCategoriesByParent.value = grouped;
  },
  { immediate: true, deep: true }
);

const onParentDragEnd = async parentKey => {
  const items = localCategoriesByParent.value[parentKey];
  if (!items || items.length < 2) return;
  const positionsHash = items.reduce((acc, item, index) => {
    acc[item.id] = index;
    return acc;
  }, {});
  try {
    await store.dispatch('customAttributeCategories/reorder', positionsHash);
  } catch {
    useAlert(t('ATTRIBUTES_MGMT.CATEGORIES.UPDATE.ERROR'));
  }
};

const collapsedParents = ref(new Set());

const toggleParent = parentKey => {
  const next = new Set(collapsedParents.value);
  if (next.has(parentKey)) next.delete(parentKey);
  else next.add(parentKey);
  collapsedParents.value = next;
};

const handleFlapoutEscape = e => {
  if (isPopoverOpen.value && e.key === 'Escape') {
    dismissFlapout();
  }
};

onMounted(() => {
  document.addEventListener('keydown', handleFlapoutEscape);
  store.dispatch('customAttributeCategories/get');
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleFlapoutEscape);
});

defineExpose({ openCreateFlapout });
</script>

<template>
  <div class="flex flex-col gap-4">
    <p class="text-sm text-n-slate-11">
      {{ $t('ATTRIBUTES_MGMT.CATEGORIES.DESCRIPTION') }}
    </p>

    <table class="min-w-full divide-y divide-n-weak">
      <thead>
        <tr class="border-b border-n-weak text-sm">
          <th class="py-4 w-8" />
          <th
            class="py-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11"
          >
            {{ $t('ATTRIBUTES_MGMT.CATEGORIES.FORM.NAME_LABEL') }}
          </th>
          <th
            class="py-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11 w-24"
          >
            {{ $t('ATTRIBUTES_MGMT.CATEGORIES.FORM.ACTIVE_LABEL') }}
          </th>
          <th class="py-4 w-32" />
        </tr>
      </thead>
      <template v-for="pc in parentCategories" :key="pc.key">
        <tbody class="divide-y divide-n-weak text-n-slate-11">
          <tr
            class="border-b border-n-weak"
            :class="
              localCategoriesByParent[parentCategoryKeyToValue[pc.key]]?.length
                ? 'cursor-pointer select-none'
                : ''
            "
            @click="
              localCategoriesByParent[parentCategoryKeyToValue[pc.key]]?.length
                ? toggleParent(parentCategoryKeyToValue[pc.key])
                : null
            "
          >
            <td class="py-4 w-8" />
            <td class="py-4">
              <div class="flex items-center gap-2">
                <span class="w-5 h-5 flex items-center justify-center shrink-0">
                  <template
                    v-if="
                      localCategoriesByParent[parentCategoryKeyToValue[pc.key]]
                        ?.length
                    "
                  >
                    <svg
                      class="size-3.5 transition-transform"
                      :class="
                        collapsedParents.has(parentCategoryKeyToValue[pc.key])
                          ? ''
                          : 'rotate-90'
                      "
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="m9 18 6-6-6-6" />
                    </svg>
                  </template>
                </span>
                <span class="font-semibold">{{ formatParentLabel(pc) }}</span>
                <span
                  v-if="
                    localCategoriesByParent[parentCategoryKeyToValue[pc.key]]
                      ?.length
                  "
                  class="text-sm"
                >
                  {{
                    localCategoriesByParent[parentCategoryKeyToValue[pc.key]]
                      .length
                  }}
                </span>
              </div>
            </td>
            <td class="py-4 text-neutral-100 dark:text-neutral-900">—</td>
            <td class="py-4">
              <div class="flex items-center justify-end">
                <Button
                  v-tooltip.top="
                    $t('ATTRIBUTES_MGMT.CATEGORIES.ASSIGNMENTS.BUTTON')
                  "
                  icon="i-lucide-users"
                  md
                  slate
                  faded
                  @click.stop="
                    openAssignmentModal(
                      parentCategoryKeyToValue[pc.key],
                      formatParentLabel(pc)
                    )
                  "
                />
              </div>
            </td>
          </tr>
        </tbody>

        <Draggable
          v-if="!collapsedParents.has(parentCategoryKeyToValue[pc.key])"
          v-model="localCategoriesByParent[parentCategoryKeyToValue[pc.key]]"
          tag="tbody"
          item-key="id"
          handle=".drag-handle"
          ghost-class="opacity-50"
          class="text-n-slate-11"
          @end="onParentDragEnd(parentCategoryKeyToValue[pc.key])"
        >
          <template #item="{ element: category }">
            <tr class="border-b border-n-weak/50 bg-n-solid-1">
              <td class="py-4 w-8" />
              <td class="py-4">
                <div class="flex items-center gap-2 pl-7">
                  <span
                    class="drag-handle shrink-0 flex items-center justify-center cursor-grab text-n-slate-9 hover:text-n-slate-11"
                  >
                    <i class="i-lucide-grip-vertical size-4" />
                  </span>
                  <span>{{ category.name }}</span>
                </div>
              </td>
              <td class="py-4">
                <Switch
                  :model-value="category.active"
                  @change="
                    store.dispatch('customAttributeCategories/update', {
                      id: category.id,
                      active: !category.active,
                    })
                  "
                />
              </td>
              <td class="py-4">
                <div class="flex items-center justify-end gap-2">
                  <Button
                    v-tooltip.top="
                      $t('ATTRIBUTES_MGMT.CATEGORIES.ASSIGNMENTS.BUTTON')
                    "
                    icon="i-lucide-users"
                    md
                    slate
                    faded
                    @click.stop="
                      openAssignmentModal(`sub_${category.id}`, category.name)
                    "
                  />
                  <Button
                    icon="i-lucide-pen-line"
                    md
                    slate
                    faded
                    @click="openEditFlapout(category, $event)"
                  />
                  <Button
                    icon="i-lucide-trash-2"
                    md
                    ruby
                    faded
                    @click="openDeleteModal(category)"
                  />
                </div>
              </td>
            </tr>
          </template>
        </Draggable>
      </template>
    </table>

    <p
      v-if="!records.length && !uiFlags.isFetching"
      class="flex-1 py-10 text-n-slate-11 flex items-center justify-center text-sm"
    >
      {{ $t('ATTRIBUTES_MGMT.CATEGORIES.EMPTY') }}
    </p>
  </div>

  <!-- Create/Edit Popover -->
  <div
    v-show="isPopoverOpen"
    ref="popoverRef"
    v-on-click-outside="() => isPopoverOpen && dismissFlapout()"
    class="fixed z-50 w-[23.75rem] max-h-[calc(100vh-1rem)] overflow-y-auto rounded-xl border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg p-4"
    :style="{
      top: `${popoverPosition.top}px`,
      left: `${popoverPosition.left}px`,
    }"
  >
    <div class="mb-4">
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{
          editingCategoryId
            ? $t('ATTRIBUTES_MGMT.CATEGORIES.UPDATE.SUCCESS').replace(
                ' successfully',
                ''
              )
            : $t('ATTRIBUTES_MGMT.CATEGORIES.ADD_BUTTON')
        }}
      </h3>
    </div>

    <div class="space-y-4">
      <div>
        <label class="block text-xs font-medium text-n-slate-11 mb-1">
          {{ $t('ATTRIBUTES_MGMT.CATEGORIES.FORM.NAME_LABEL') }}
        </label>
        <input
          v-model="form.name"
          type="text"
          :placeholder="$t('ATTRIBUTES_MGMT.CATEGORIES.FORM.NAME_PLACEHOLDER')"
          class="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:!bg-slate-800"
        />
      </div>
      <div>
        <label class="block text-xs font-medium text-n-slate-11 mb-1">
          {{ $t('ATTRIBUTES_MGMT.CATEGORIES.FORM.PARENT_CATEGORY_LABEL') }}
        </label>
        <select
          v-model="form.parent_category"
          class="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:!bg-slate-800"
        >
          <option
            v-for="pc in parentCategories"
            :key="pc.key"
            :value="parentCategoryKeyToValue[pc.key]"
          >
            {{ formatParentLabel(pc) }}
          </option>
        </select>
      </div>
      <div class="flex items-center gap-3 py-2">
        <Switch v-model="form.active" />
        <span class="text-sm font-semibold text-n-slate-12">
          {{ $t('ATTRIBUTES_MGMT.CATEGORIES.FORM.ACTIVE_LABEL') }}
        </span>
      </div>
    </div>

    <div class="mt-4 flex items-center gap-2">
      <Button
        label="Cancel"
        slate
        outlined
        class="w-full"
        @click="cancelFlapout"
      />
      <Button
        :label="editingCategoryId ? 'Save' : 'Create'"
        class="w-full"
        :is-loading="uiFlags.isCreating || uiFlags.isUpdating"
        @click="saveCategory"
      />
    </div>
  </div>

  <!-- Delete Confirmation Modal -->
  <div
    v-if="showDeleteModal"
    class="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm"
    @click.self="closeDeleteModal"
  >
    <div
      class="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 shadow-2xl p-6 mx-4"
    >
      <h3 class="text-base font-semibold text-slate-900 dark:text-white">
        {{ $t('ATTRIBUTES_MGMT.CATEGORIES.DELETE.CONFIRM_TITLE') }}
      </h3>
      <p class="mt-2 text-sm text-slate-600 dark:text-slate-300">
        {{
          $t('ATTRIBUTES_MGMT.CATEGORIES.DELETE.CONFIRM_MESSAGE', {
            name: selectedCategory?.name,
          })
        }}
      </p>
      <div class="mt-5 flex items-center gap-2">
        <Button
          label="Cancel"
          slate
          outlined
          class="w-full"
          @click="closeDeleteModal"
        />
        <Button
          label="Delete"
          ruby
          class="w-full"
          :is-loading="uiFlags.isDeleting"
          @click="removeCategory"
        />
      </div>
    </div>
  </div>

  <CategoryAssignmentModal
    v-if="assignmentModal"
    :category-key="assignmentModal.categoryKey"
    :category-label="assignmentModal.categoryLabel"
    @close="closeAssignmentModal"
  />
</template>
